const THEME_VARIABLES = [
  "--accent",
  "--accent-dark",
  "--accent-soft",
  "--text",
  "--muted",
  "--surface",
  "--border",
  "--page-bg",
  "--button-bg",
  "--button-text",
  "--home-primary",
  "--home-link",
  "--home-button-bg",
  "--home-button-text",
  "--home-muted",
];

function hexToRgb(hex) {
  const color = String(hex || "").replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(color)) return null;
  return {
    r: parseInt(color.slice(0, 2), 16),
    g: parseInt(color.slice(2, 4), 16),
    b: parseInt(color.slice(4, 6), 16),
  };
}

function isDarkColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 < 140;
}

export function storeThemeFromSettings(settings) {
  if (!settings) return null;
  const hasTheme = Boolean(settings.themeId);
  const hasColors = Boolean(
    settings.primaryColor ||
      settings.backgroundColor ||
      settings.buttonColor ||
      settings.textColor
  );
  if (!hasTheme && !hasColors) return null;
  return {
    themeId: settings.themeId || "custom",
    themeDark: Boolean(settings.themeDark),
    backgroundColor: settings.backgroundColor || "#ffffff",
    textColor: settings.textColor || "#111827",
    primaryColor: settings.primaryColor || "#111827",
    linkColor: settings.linkColor || settings.primaryColor || "#111827",
    buttonColor: settings.buttonColor || settings.primaryColor || "#111827",
    buttonTextColor: settings.buttonTextColor || "#ffffff",
    mutedColor: settings.mutedColor || "#6b7280",
    primaryGradientStart: settings.primaryGradientStart || "",
    primaryGradientEnd: settings.primaryGradientEnd || "",
  };
}

export function storeThemeVars(theme) {
  const dark = Boolean(theme.themeDark) || isDarkColor(theme.backgroundColor);
  const primary = theme.primaryColor;
  const buttonBackground =
    theme.primaryGradientStart && theme.primaryGradientEnd
      ? `linear-gradient(135deg, ${theme.primaryGradientStart}, ${theme.primaryGradientEnd})`
      : theme.buttonColor || primary;
  const rgb = hexToRgb(primary);

  return {
    "--accent": primary,
    "--accent-dark": primary,
    "--accent-soft": rgb
      ? `rgb(${rgb.r} ${rgb.g} ${rgb.b} / 14%)`
      : "rgb(99 91 255 / 12%)",
    "--text": theme.textColor,
    "--muted": theme.mutedColor,
    "--surface": dark ? "#111111" : "#ffffff",
    "--border": dark ? "#27272a" : "#e4e7ec",
    "--page-bg": theme.backgroundColor,
    "--button-bg": buttonBackground,
    "--button-text": theme.buttonTextColor,
    "--home-primary": primary,
    "--home-link": theme.linkColor,
    "--home-button-bg": buttonBackground,
    "--home-button-text": theme.buttonTextColor,
    "--home-muted": theme.mutedColor,
  };
}

export function applyStoreTheme(theme) {
  const root = document.documentElement;
  if (!theme) {
    clearStoreTheme();
    return;
  }

  root.dataset.storeTheme = theme.themeId;
  const vars = storeThemeVars(theme);
  Object.entries(vars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

function themeCacheKey(storeSlug) {
  return `atomicpay.storefront.theme.${storeSlug}`;
}

export function readCachedStoreTheme(storeSlug) {
  if (!storeSlug || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(themeCacheKey(storeSlug));
    const payload = raw ? JSON.parse(raw) : null;
    return payload?.theme || payload || null;
  } catch {
    return null;
  }
}

export function writeCachedStoreTheme(storeSlug, theme) {
  if (!storeSlug || typeof localStorage === "undefined") return;
  try {
    if (theme) {
      localStorage.setItem(
        themeCacheKey(storeSlug),
        JSON.stringify({ theme, vars: storeThemeVars(theme) })
      );
    } else {
      localStorage.removeItem(themeCacheKey(storeSlug));
    }
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearStoreTheme() {
  const root = document.documentElement;
  delete root.dataset.storeTheme;
  THEME_VARIABLES.forEach((name) => root.style.removeProperty(name));
}
